import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, Coins, ArrowLeft, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { useSubscription } from "@/hooks/useSubscription";
import { useTestClock } from "@/contexts/TestClockContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

const tiers = [
  {
    name: "Starter",
    tier: "starter",
    price: { monthly: "€5", annual: "€50" },
    credits: 200,
    description: "For professionals who need basic email extraction",
    features: [
      { name: "200 AI credits/month", included: true },
      { name: "Email extraction (from 1 credit)", included: true },
      { name: "Settings dashboard", included: true },
      { name: "Email support", included: true },
      { name: "Smart Categories", included: false },
      { name: "Smart Notes", included: false },
      { name: "Field Presets", included: false },
    ],
    popular: false,
  },
  {
    name: "Pro",
    tier: "pro",
    price: { monthly: "€19", annual: "€190" },
    credits: 1000,
    description: "The complete AI assistant for power users",
    features: [
      { name: "1,000 AI credits/month", included: true },
      { name: "Email extraction", included: true },
      { name: "Smart Categories (up to 10)", included: true },
      { name: "Smart Notes (up to 10 tasks)", included: true },
      { name: "Field Presets (unlimited)", included: true },
      { name: "Priority email support", included: true },
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    tier: "enterprise",
    price: { monthly: "Custom", annual: "Custom" },
    credits: 5000,
    description: "Need more credits or custom solutions?",
    features: [
      { name: "Custom credit volume", included: true },
      { name: "All Pro features", included: true },
      { name: "Tailored solutions", included: true },
      { name: "Dedicated support", included: true },
    ],
    popular: false,
  },
];

const topupPackages = [
  { name: "Mini", type: "mini", credits: 100, price: 5 },
  { name: "Standard", type: "standard", credits: 500, price: 18, discount: "10% off", popular: false },
  { name: "Power", type: "power", credits: 2000, price: 55, discount: "30% off", popular: true },
  { name: "Agency", type: "agency", credits: 10000, price: 220, discount: "45% off", popular: false },
];

export default function Plans() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [loading, setLoading] = useState<string | null>(null);
  const { subscription, isLoading: subLoading } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubscribe = async (tier: string) => {
    if (tier === "enterprise") {
      navigate("/support#contact");
      return;
    }
    setLoading(`${tier}_${billingCycle}`);

    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: { tier, billing_cycle: billingCycle },
      });
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Subscription Already Active", description: data.error, variant: "destructive" });
        return;
      }
      if (data?.is_billing_portal) {
        toast({ title: "Opening Customer Portal", description: "You can change your plan in the next window" });
        if (data?.url) {
          window.open(data.url, '_blank');
          return;
        }
      }
      if (data?.url) window.open(data.url, '_blank');
    } catch (error) {
      console.error('Subscription error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      let description = 'Could not start checkout. Please try again.';
      if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        description = 'Connection failed. Please check your internet and try again.';
      } else if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
        description = 'Please sign in again to continue.';
      }
      toast({ title: "Checkout Error", description, variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  const handleBuyTopup = async (packageType: string) => {
    setLoading(packageType);
    try {
      const { data, error } = await supabase.functions.invoke('create-topup-payment', {
        body: { packageType },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (error) {
      console.error('Topup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      let description = 'Could not start payment. Please try again.';
      if (errorMessage.includes('subscription')) {
        description = 'You need an active subscription to purchase top-ups.';
      }
      toast({ title: "Payment Error", description, variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  const isWelcomeTier = subscription?.tier === 'welcome';
  const hasActiveSubscription = subscription !== null &&
    !isWelcomeTier &&
    (subscription?.status === 'active' ||
     (subscription?.status === 'cancelled' && subscription?.current_period_end && new Date(subscription.current_period_end) > new Date()));

  if (subLoading) {
    return (
      <div className="min-h-screen flex flex-col app-surface">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <p className="text-muted-foreground">Loading your subscription…</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col app-surface">
      <SEO
        title="Pricing — Email2Contact for Outlook"
        description="Email2Contact pricing: Starter €5/mo, Pro €19/mo, Enterprise custom. Pay-as-you-go credit top-ups. GDPR-compliant, EU-hosted AI, cancel anytime."
        path="/plans"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Pricing", path: "/plans" },
        ])}
      />
      <Navbar />
      <main className="flex-grow bg-dot-grid">
        <section className="container px-4 py-12 md:py-20">
          {/* Back to Settings */}
          <div className="mb-8">
            <Button asChild variant="outline" size="sm">
              <Link to="/settings" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Settings
              </Link>
            </Button>
          </div>

          {/* Reactivation Banner for Cancelled Subscriptions */}
          {subscription?.status === 'cancelled' && (
            <div className="app-info-box mb-8 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-foreground mb-1">
                    {new Date(subscription.current_period_end) > new Date()
                      ? "Welcome back"
                      : "We missed you"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(subscription.current_period_end) > new Date()
                      ? `Your current plan ends on ${format(new Date(subscription.current_period_end), 'PPP')}. Choose a new subscription below to continue without interruption.`
                      : "Your subscription has ended. Select a plan below to regain access to all premium features."}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => document.getElementById('subscription-tiers')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See plans below ↓
              </Button>
            </div>
          )}

          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">
              Pricing
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mb-4 tracking-tight">
              Choose your plan
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Credit-based system that scales with your workflow. No hidden fees. Cancel anytime.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-muted p-1 rounded-lg border border-border">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={cn(
                  "px-4 py-1.5 rounded-md text-sm transition-all",
                  billingCycle === "monthly"
                    ? "bg-background shadow-fluent-2 font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={cn(
                  "px-4 py-1.5 rounded-md text-sm transition-all flex items-center gap-2",
                  billingCycle === "annual"
                    ? "bg-background shadow-fluent-2 font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Annual
                <span className="app-pill app-pill-success !py-0">Save 2 months</span>
              </button>
            </div>
          </div>

          {/* Subscription Tiers */}
          <div id="subscription-tiers" className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto mb-20">
            {tiers.map((tier) => {
              const isCurrentTier = subscription?.tier === tier.tier && !isWelcomeTier;
              const isCurrentCycle = subscription?.billing_cycle === billingCycle && !isWelcomeTier;
              const isCurrentPlan = isCurrentTier && isCurrentCycle;
              const isCycleChange = isCurrentTier && !isCurrentCycle;

              const tierOrder = { starter: 1, pro: 2, enterprise: 3 };
              const currentTierLevel = subscription ? tierOrder[subscription.tier as keyof typeof tierOrder] : 0;
              const thisTierLevel = tierOrder[tier.tier as keyof typeof tierOrder];
              const isUpgrade = thisTierLevel > currentTierLevel;
              const isDowngrade = thisTierLevel < currentTierLevel;

              let buttonText = `Subscribe to ${tier.name}`;
              let buttonVariant: "default" | "outline" = tier.popular ? "default" : "outline";

              if (isCurrentPlan) buttonText = "Current plan";
              else if (isCycleChange) {
                buttonText = `Switch to ${billingCycle === 'monthly' ? 'monthly' : 'annual'}`;
                buttonVariant = "outline";
              } else if (isUpgrade) {
                buttonText = `Upgrade to ${tier.name}`;
                buttonVariant = "default";
              } else if (isDowngrade) {
                buttonText = `Downgrade to ${tier.name}`;
                buttonVariant = "outline";
              } else if (tier.name === "Enterprise") {
                buttonText = "Contact sales";
              }

              return (
                <div
                  key={tier.name}
                  className={cn(
                    "app-tile p-6 relative transition-all",
                    tier.popular && "ring-1 ring-primary/30 shadow-fluent-16",
                    isCurrentPlan && "ring-1 ring-emerald-500/40"
                  )}
                >
                  {tier.popular && (
                    <div className="absolute inset-x-0 top-0 h-px bg-primary rounded-t-xl" />
                  )}
                  {tier.popular && (
                    <div className="absolute -top-3 left-6">
                      <span className="app-pill app-pill-primary !bg-primary !text-primary-foreground border-primary">
                        <Sparkles className="h-3 w-3" />
                        Most popular
                      </span>
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-3 right-6">
                      <span className="app-pill app-pill-success">
                        <Check className="h-3 w-3" />
                        Your plan
                      </span>
                    </div>
                  )}

                  <div className="text-center pb-4">
                    <h3 className="app-section-title text-xl mb-2">{tier.name}</h3>
                    <div className="mb-2">
                      <span className="font-display text-4xl font-semibold tracking-tight">
                        {tier.price[billingCycle]}
                      </span>
                      {tier.name !== "Enterprise" && (
                        <>
                          <span className="text-muted-foreground text-sm">
                            /{billingCycle === "monthly" ? "mo" : "yr"}
                          </span>
                          <p className="text-xs text-muted-foreground mt-1">plus 19% VAT</p>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                    <div className="flex items-center justify-center gap-2 mt-3 text-sm">
                      <Coins className="w-4 h-4 text-primary" />
                      <span className="font-medium tabular-nums">{tier.credits.toLocaleString()} credits/month</span>
                    </div>
                  </div>

                  <div className="border-t border-border/60 pt-4 space-y-3">
                    <ul className="space-y-2.5">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-4 h-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                          )}
                          <span className={feature.included ? "text-foreground" : "text-muted-foreground line-through"}>
                            {feature.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full mt-4"
                      variant={buttonVariant}
                      onClick={() => handleSubscribe(tier.tier)}
                      disabled={loading === `${tier.tier}_${billingCycle}` || isCurrentPlan}
                    >
                      {loading === `${tier.tier}_${billingCycle}` ? "Opening…" : buttonText}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Top-ups Section */}
          {hasActiveSubscription && (
            <div className="max-w-6xl mx-auto pt-10 border-t border-border">
              <div className="text-center mb-10">
                <h2 className="font-display text-3xl font-semibold mb-3 tracking-tight">Need more credits?</h2>
                <p className="text-muted-foreground">Top up your account with additional credits anytime.</p>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                {topupPackages.map((pkg) => (
                  <div
                    key={pkg.name}
                    className={cn(
                      "app-tile p-5 relative",
                      pkg.popular && "ring-1 ring-primary/30"
                    )}
                  >
                    {pkg.popular && (
                      <div className="absolute inset-x-0 top-0 h-px bg-primary rounded-t-xl" />
                    )}
                    {pkg.discount && (
                      <div className="absolute -top-2.5 right-4">
                        <span className="app-pill app-pill-success">{pkg.discount}</span>
                      </div>
                    )}
                    <div className="text-center pb-3">
                      <h3 className="app-section-title mb-1">{pkg.name}</h3>
                      <div className="mb-1">
                        <span className="font-display text-2xl font-semibold">€{pkg.price}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">plus 19% VAT</p>
                      </div>
                      <div className="flex items-center justify-center gap-1.5 text-sm">
                        <Coins className="w-3.5 h-3.5 text-primary" />
                        <span className="font-medium tabular-nums">{pkg.credits.toLocaleString()}</span>
                      </div>
                    </div>
                    <Button
                      className="w-full mt-3"
                      variant={pkg.popular ? "default" : "outline"}
                      onClick={() => handleBuyTopup(pkg.type)}
                      disabled={loading === pkg.type}
                    >
                      {loading === pkg.type ? "Opening…" : "Buy credits"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Preview */}
          <div className="mt-20 max-w-2xl mx-auto text-center">
            <h2 className="font-display text-2xl font-semibold mb-3 tracking-tight">Have questions?</h2>
            <p className="text-muted-foreground mb-6">
              Check out our FAQ or contact support for help choosing the right plan.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/support#faq")}>View FAQ</Button>
              <Button onClick={() => navigate("/support")}>Contact support</Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
